<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { useArrayMap, whenever } from '@vueuse/core';
import { secondsToMilliseconds } from 'date-fns';
import { enum as zodEnum } from 'zod';

import { useCurrentSignatureRequestKey, useRouteSubSteps, isObjsShallowEqual, useFlux } from '~~core';
import { SecureEnvironment } from '~~base-ui';

import type { RouteLocationNormalized } from '#vue-router';

import { useGeolocationPermission } from '../../../composables/use-geolocation-permission';
import TermsOfAgreement from '../../../components/TermsOfAgreement/TermsOfAgreement.vue';
import type { ValidRequirementType } from '../../../composables/use-requirement-steps';
import { REQUIREMENT_STEP_KEY } from '../../../composables/use-requirement-step';
import { REQUIREMENT_UI_KEY } from '../../../composables/use-requirement-ui';
import { requirementQueryKeys, useFetchRequirementSteps } from '../../../repositories/requirement';
import { fetchRequirement } from '../../../services/requirement';
import { useRequirementSteps } from '../../../composables/use-requirement-steps';
import RequirementToolbar from '../../../components/RequirementToolbar/RequirementToolbar.vue';

type RouteLocation = {
  name: string;
  params?: Record<string, string>;
};

function validateSignatureRequestKindParam() {
  const route = useRoute();
  const validSignatureRequestKindsSchema = zodEnum(['notarial', 'flux']);
  // @ts-expect-error Nuxt typed routes is experimental and cant infer the param "signatureRequestKind" at the current version
  const parseResult = validSignatureRequestKindsSchema.safeParse(route.params.signatureRequestKind);

  if (!parseResult.success) throw createError({ fatal: true, statusCode: 404 });

  return parseResult.data;
}

function useRoutesAndRequirementNames() {
  const localeRoute = useLocaleRoute();
  const route = useRoute();
  const getRouteBaseName = useRouteBaseName();
  const tokenRouteName = 'signatureRequestKind-signatureRequestKey-requirement-token-tokenKind';

  const stepToRouteMap: Record<ValidRequirementType, RouteLocation> = {
    whatsapp: { name: tokenRouteName, params: { tokenKind: 'whatsapp' } },
    sms: { name: tokenRouteName, params: { tokenKind: 'sms' } },
    email: { name: tokenRouteName, params: { tokenKind: 'email' } },
    federal_data: { name: 'signatureRequestKind-signatureRequestKey-requirement-information' },
    handwritten: { name: 'signatureRequestKind-signatureRequestKey-requirement-handwritten' },
    official_document: { name: 'signatureRequestKind-signatureRequestKey-requirement-official-doc' },
    selfie: { name: 'signatureRequestKind-signatureRequestKey-requirement-selfie-plus-doc' },
    address_proof: { name: 'signatureRequestKind-signatureRequestKey-requirement-address-proof' },
    icp_brasil: { name: 'signatureRequestKind-signatureRequestKey-requirement-digital-certificate' },
    pix: { name: 'signatureRequestKind-signatureRequestKey-requirement-pix' },
    liveness: { name: 'signatureRequestKind-signatureRequestKey-requirement-liveness' },
    facial_biometrics: { name: 'signatureRequestKind-signatureRequestKey-requirement-facial-biometrics' },
    biometric: { name: 'signatureRequestKind-signatureRequestKey-requirement-biometric' },
    documentscopy: { name: 'signatureRequestKind-signatureRequestKey-requirement-documentscopy' },
  };

  const routeNameToStep = Object.entries(stepToRouteMap);

  function getLocationFromRequirement(step: ValidRequirementType) {
    const selectedRoute = stepToRouteMap[step];

    const location = localeRoute({
      name: selectedRoute.name,
      query: route.query,
      params: {
        ...route.params,
        ...selectedRoute.params,
      },
    });

    if (!location) throw new Error(`Requirement route not found for step="${step}"`);

    return location;
  }

  function getRequirementFromLocation(location: RouteLocationNormalized) {
    const baseRouteName = getRouteBaseName(location);

    if (!baseRouteName) throw new Error(`Not found requirement to route name "${baseRouteName}"`);

    const { signatureRequestKey, signatureRequestKind, ...locationParams } = location.params;

    const isSameRoute = (routeInfo: RouteLocation) => routeInfo.name === baseRouteName && isObjsShallowEqual(locationParams, routeInfo.params ?? {});
    const [requirement] = routeNameToStep.find(([, routeInfo]) => isSameRoute(routeInfo)) ?? [];

    return requirement;
  }

  return {
    getLocationFromRequirement,
    getRequirementFromLocation,
  };
}

const getRouteBaseName = useRouteBaseName();
const localePath = useLocalePath();
const route = useRoute();
const { requestGeolocationPermission } = useGeolocationPermission();
const queryClient = useQueryClient();
const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { steps, suspense, refetch: refetchRequirementSteps } = useFetchRequirementSteps(signatureRequestKey);
const { validSteps } = useRequirementSteps(steps);
const { getLocationFromRequirement, getRequirementFromLocation } = useRoutesAndRequirementNames();
const routeSteps = useArrayMap(validSteps, getLocationFromRequirement);
const {
  goToNextSubStep,
  goToPreviousSubStep,
  nextSubStep,
  previousSubStep,
  resetSubSteps,
  currentSubStep,
} = useRouteSubSteps(routeSteps);
const { isFlux } = useFlux();

const isInRequirementRoot = computed(() => getRouteBaseName(route) === 'signatureRequestKind-signatureRequestKey-requirement');
const isInSummaryPage = computed(() => getRouteBaseName(route) === 'signatureRequestKind-signatureRequestKey-requirement-summary');
const isFirstRequirement = computed(() => !previousSubStep.value);
const isLastRequirement = computed(() => !nextSubStep.value);

async function prefetchNextStep() {
  const currentStepName = getRequirementFromLocation(route);
  const currentIndex = currentStepName ? validSteps.value.indexOf(currentStepName) : -1;
  const nextStepName = currentIndex === -1 ? undefined : validSteps.value[currentIndex + 1];

  if (!nextStepName) return; // Steps ended, do not prefetch, but keep the current page

  await queryClient.prefetchQuery({
    queryKey: requirementQueryKeys.show(signatureRequestKey, nextStepName).queryKey,
    queryFn: () => fetchRequirement(nextStepName, signatureRequestKey.value),
    staleTime: secondsToMilliseconds(30),
  });
}

async function finishRequirement() {
  if (isLastRequirement.value) {
    return navigateTo(localePath({
      name: 'signatureRequestKind-signatureRequestKey-finish',
      params: route.params,
      query: route.query,
    }));
  }

  await goToNextSubStep();
  await refetchRequirementSteps();
  prefetchNextStep();
}

async function handleGoBack() {
  const wasPrevious = await goToPreviousSubStep();
  const pathName = isInSummaryPage.value ? 'signatureRequestKind-signatureRequestKey-documents' : 'signatureRequestKind-signatureRequestKey-requirement-summary';

  if (wasPrevious) return;

  return navigateTo(localePath({
    name: pathName,
    query: route.query,
    params: route.params,
  }));
}

async function resetSession() {
  await suspense();

  const isInInvalidStep = !isInSummaryPage.value && !currentSubStep.value;

  if (!isInRequirementRoot.value && isInInvalidStep) throw createError({ statusCode: 404, fatal: true });
  if (!isInRequirementRoot.value) return prefetchNextStep();

  await resetSubSteps();
  await prefetchNextStep();
}

onBeforeMount(() => resetSession());
onMounted(() => {
  validateSignatureRequestKindParam();
  requestGeolocationPermission();
});
whenever(isInRequirementRoot, () => resetSession());
provide(REQUIREMENT_UI_KEY, { isLastRequirement });
provide(REQUIREMENT_STEP_KEY, { finishRequirement });
</script>

<template>
  <div :class="$style.requirement">
    <RequirementToolbar
      :length="validSteps.length"
      :current="routeSteps.findIndex(r => r.name === currentSubStep?.name) + 1"
      :hide-progress="!currentSubStep"
      :hide-back-button="isFlux && isFirstRequirement"
      @go-back="handleGoBack()"
    />

    <div :class="$style.stepWrapper">
      <NuxtPage />
      <TermsOfAgreement v-if="isLastRequirement" />
      <SecureEnvironment />
    </div>
  </div>
</template>

<style lang="scss" module>
.requirement {
  --step-max-width: 496px;
}

.stepWrapper {
  padding: var(--space-regular) var(--space-regular) var(--space-large) var(--space-regular);
}
</style>
